import { AppCheckbox, AppTable, AppTooltip } from '@/components/atoms';
import type { RezeptListItem } from '@/types/rezept.types';
import { useTheme } from '@shared/providers/theme/ThemeContext';
import type { ColumnsType } from 'antd/es/table';
import type { TableRowSelection } from 'antd/es/table/interface';
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { TABLE_SCROLL_Y } from '../constants';
import {
  formatLateReturnBadge,
  formatRezeptKbn,
  formatStatus,
  formatTreatementYearmonth,
} from '../utils';

interface Props {
  rows: RezeptListItem[];
  isLoading: boolean;
  selectedIds: Set<number>;
  duplicateTreatmentReportFeeIds: Set<number>;
  hasFilters: boolean;
  onSelectionChange: (ids: number[]) => void;
  onDoubleClickRow: (row: RezeptListItem) => void;
  onToggleTreatmentReport: (id: number, value: 0 | 1) => void;
}

const ROW_CLICK_DELAY_MS = 220;
const INTERACTIVE_ROW_TARGET_SELECTOR = [
  'a',
  'button',
  'input',
  'label',
  'textarea',
  'select',
  '[role="button"]',
  '[data-row-action="true"]',
  '.ant-checkbox-wrapper',
  '.ant-table-selection-column',
].join(', ');

const shouldCellUpdate = (record: RezeptListItem, prevRecord: RezeptListItem) =>
  record !== prevRecord;

const SELECTION_COLUMN_WIDTH = 56;
/** Narrowest shared column the virtual table is used for: cell padding + border is 17px. */
const MIN_VIRTUAL_SHARED_WIDTH = 24;

/** Width the browser's own vertical scrollbar takes (0 with overlay scrollbars). */
const measureNativeScrollbarWidth = (): number => {
  const probe = document.createElement('div');
  probe.style.cssText = 'position:absolute;top:-9999px;width:100px;height:100px;overflow:scroll';
  document.body.appendChild(probe);
  const width = probe.offsetWidth - probe.clientWidth;
  probe.remove();
  return width;
};

/** Stands in for the plain table's header scrollbar gutter (th.ant-table-cell-scrollbar). */
const SCROLLBAR_GUTTER_KEY = '__scrollbar_gutter';

/**
 * Virtual mode needs every column width as a number. Reproduce the split the plain <table>
 * produced under AppTable enableEllipsis: numeric widths >= 80 were dropped there and those
 * columns shared what was left equally; the others (numbers < 80, 'NNpx' strings) kept their
 * width. Where the browser's scrollbar takes layout space, the plain table gave that strip to a
 * header gutter cell and the native scrollbar; here an empty last column of the same width plays
 * the gutter (the virtual scrollbar is drawn over its body cells), so the columns add up to the
 * container exactly and antd has no remainder to pour into the last real column.
 * Integer widths: the virtual table rounds them anyway, this keeps the rounding in our hands.
 */
const isSharedWidth = (width: unknown) => typeof width === 'number' && width >= 80;

/** Space the plain table left for its shared (auto-width) columns; <= 0 means they collapse. */
const sharedColumnSpace = (
  columns: ColumnsType<RezeptListItem>,
  containerWidth: number,
  scrollbarWidth: number
): number => {
  const fixedWidth = columns.reduce(
    (sum, col) => (isSharedWidth(col.width) ? sum : sum + parseFloat(String(col.width))),
    SELECTION_COLUMN_WIDTH
  );
  return containerWidth - scrollbarWidth - fixedWidth;
};

const fitColumnWidths = (
  columns: ColumnsType<RezeptListItem>,
  containerWidth: number,
  scrollbarWidth: number
): ColumnsType<RezeptListItem> => {
  const isShared = isSharedWidth;
  const sharedCount = columns.filter(col => isShared(col.width)).length;
  const available = Math.max(0, sharedColumnSpace(columns, containerWidth, scrollbarWidth));
  const share = Math.floor(available / sharedCount);
  let remainder = available - share * sharedCount;
  const fitted: ColumnsType<RezeptListItem> = columns.map(col => {
    if (!isShared(col.width)) return { ...col, width: parseFloat(String(col.width)) };
    const extra = remainder > 0 ? 1 : 0;
    remainder -= extra;
    return { ...col, width: share + extra };
  });
  if (scrollbarWidth > 0) {
    fitted.push({
      key: SCROLLBAR_GUTTER_KEY,
      width: scrollbarWidth,
      ellipsisTooltip: false,
      className: 'rezept-list-scrollbar-gutter',
      render: () => null,
      shouldCellUpdate: () => false,
    } as ColumnsType<RezeptListItem>[number]);
  }
  return fitted;
};

const isInteractiveRowTarget = (target: EventTarget | null) =>
  target instanceof Element && !!target.closest(INTERACTIVE_ROW_TARGET_SELECTOR);

const RezeptListTable: React.FC<Props> = ({
  rows,
  isLoading,
  selectedIds,
  duplicateTreatmentReportFeeIds,
  hasFilters,
  onSelectionChange,
  onDoubleClickRow,
  onToggleTreatmentReport,
}) => {
  const navigate = useNavigate();
  const { siteType } = useTheme();
  const selectedIdsRef = useRef(selectedIds);
  const rowClickTimersRef = useRef<Map<number, number>>(new Map());

  useEffect(() => {
    selectedIdsRef.current = selectedIds;
  }, [selectedIds]);

  // BD: clicking "未" → opens the create-report screen "with the month pre-filled" → pass ym via query
  // so CreateTreatmentReportPage pre-fills treatment_yearmonth.
  const toCreate = useCallback(
    (patientId: number, ym?: string) =>
      `/${siteType}/treatment-reports/${patientId}/create${ym ? `?ym=${ym}` : ''}`,
    [siteType]
  );
  const toEdit = useCallback(
    (patientId: number, reportId: number) =>
      `/${siteType}/treatment-reports/${patientId}/edit/${reportId}`,
    [siteType]
  );

  const selectedRowKeys = useMemo(() => Array.from(selectedIds), [selectedIds]);

  const toggleSelectedRow = useCallback(
    (id: number) => {
      const next = new Set(selectedIdsRef.current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      onSelectionChange(Array.from(next));
    },
    [onSelectionChange]
  );

  const clearRowClickTimer = useCallback((id: number) => {
    const timer = rowClickTimersRef.current.get(id);
    if (timer) {
      window.clearTimeout(timer);
      rowClickTimersRef.current.delete(id);
    }
  }, []);

  useEffect(
    () => () => {
      rowClickTimersRef.current.forEach(timer => window.clearTimeout(timer));
      rowClickTimersRef.current.clear();
    },
    []
  );

  const handleRowClick = useCallback(
    (row: RezeptListItem, event: React.MouseEvent<HTMLElement>) => {
      if (isInteractiveRowTarget(event.target)) return;

      if (event.detail > 1) {
        clearRowClickTimer(row.id);
        return;
      }

      clearRowClickTimer(row.id);
      const timer = window.setTimeout(() => {
        toggleSelectedRow(row.id);
        rowClickTimersRef.current.delete(row.id);
      }, ROW_CLICK_DELAY_MS);
      rowClickTimersRef.current.set(row.id, timer);
    },
    [clearRowClickTimer, toggleSelectedRow]
  );

  const handleRowDoubleClick = useCallback(
    (row: RezeptListItem, event: React.MouseEvent<HTMLElement>) => {
      if (isInteractiveRowTarget(event.target)) return;
      clearRowClickTimer(row.id);
      onDoubleClickRow(row);
    },
    [clearRowClickTimer, onDoubleClickRow]
  );

  const rowSelection = useMemo<TableRowSelection<RezeptListItem>>(
    () => ({
      selectedRowKeys,
      onChange: keys => onSelectionChange(keys.map(Number)),
      columnWidth: SELECTION_COLUMN_WIDTH,
      align: 'center',
      getTitleCheckboxProps: () => ({ 'aria-label': '全選択' }),
      renderCell: (_checked, _record, _index, originNode) => (
        <span onClick={e => e.stopPropagation()} onDoubleClick={e => e.stopPropagation()}>
          {originNode}
        </span>
      ),
    }),
    [onSelectionChange, selectedRowKeys]
  );

  const columns: ColumnsType<RezeptListItem> = useMemo(() => {
    const baseColumns: ColumnsType<RezeptListItem> = [
      {
        title: '番号',
        dataIndex: 'serial_number',
        key: 'serial_number',
        width: 56,
      },
      {
        title: '受診者名',
        dataIndex: 'patient_name',
        key: 'patient_name',
        width: 170,
      },
      {
        title: '保険者名',
        dataIndex: 'insurer_name',
        key: 'insurer_name',
        width: 150,
      },
      {
        title: '施術年月',
        dataIndex: 'treatement_yearmonth',
        key: 'treatement_yearmonth',
        width: 120,
        render: value => formatTreatementYearmonth(value),
      },
      {
        title: '本・関',
        dataIndex: 'rezept_kbn',
        key: 'rezept_kbn',
        width: 56,
        align: 'center',
        render: (value, row) => formatRezeptKbn(value, row.nonoutput_rezept_medical_aid_number),
      },
      {
        title: '遅・返',
        key: 'late_return',
        width: 56,
        align: 'center',
        render: (_, row) => formatLateReturnBadge(row),
      },
      {
        title: '用紙',
        dataIndex: 'rezept_template_name',
        key: 'rezept_template_name',
        width: 64,
      },
      {
        title: '同意連番',
        dataIndex: 'consent_serial_number',
        key: 'consent_serial_number',
        width: 70,
      },
      {
        title: '報告料',
        key: 'add_treatment_report',
        width: 56,
        align: 'center',
        render: (_, row) => {
          const hasReport = !!row.is_treatment_report || row.is_treatment_report2 === 2;
          if (!hasReport) return null;
          // 関レセ mirrors its 本レセ and cannot be ticked on its own (HIT-7339).
          const locked =
            row.status === 1 ||
            row.status === 2 ||
            !!row.is_treatment_report_default ||
            row.rezept_kbn === 2;
          const checked = row.add_treatment_report === 1;
          return (
            <AppCheckbox
              checked={checked}
              disabled={locked}
              onChange={e => {
                if (!locked) onToggleTreatmentReport(row.id, e.target.checked ? 1 : 0);
              }}
              onClick={e => e.stopPropagation()}
            />
          );
        },
      },
      {
        title: '報告書',
        key: 'report_link',
        // String width so AppTable's >=80 number-width auto-distribute is skipped —
        // keeps this sparse column compact instead of inflating to an equal share.
        width: '80px',
        render: (_, row) => {
          const linkStyle = 'text-blue-600 underline cursor-pointer text-sm';

          // Report 1
          let report1: React.ReactNode = null;
          if (row.is_treatment_report && row.treatment_report_id) {
            const isManual = row.first_report_maker === '手動';
            report1 = (
              <span
                className={linkStyle}
                data-row-action="true"
                onClick={e => {
                  e.stopPropagation();
                  navigate(toEdit(row.patient_id, row.treatment_report_id!));
                }}
                onDoubleClick={e => e.stopPropagation()}
              >
                作成済{isManual && <span className="text-red-500">●</span>}
              </span>
            );
          } else if (row.is_treatment_report2 === 2) {
            // Pass treatement_report_month (the month the system determined needs reporting)
            // — not treatement_yearmonth, because when consent.orthomorphia=null
            // report_month can differ (= consent.term_to month).
            const reportYm = row.treatement_report_month || row.treatement_yearmonth;
            report1 = (
              <span
                className={linkStyle}
                data-row-action="true"
                onClick={e => {
                  e.stopPropagation();
                  navigate(toCreate(row.patient_id, reportYm));
                }}
                onDoubleClick={e => e.stopPropagation()}
              >
                未
              </span>
            );
          }

          // Report 2 (only when second_report=true AND second_report_id != 0)
          let report2: React.ReactNode = null;
          if (row.second_report && row.second_report_id) {
            const isManual2 = row.second_report_maker === '手動';
            report2 = (
              <span
                className={linkStyle}
                data-row-action="true"
                onClick={e => {
                  e.stopPropagation();
                  navigate(toEdit(row.patient_id, row.second_report_id!));
                }}
                onDoubleClick={e => e.stopPropagation()}
              >
                作成済{isManual2 && <span className="text-red-500">●</span>}
              </span>
            );
          }

          if (!report1 && !report2) return null;
          return (
            <div className="flex flex-col gap-0.5">
              {report1}
              {report2}
            </div>
          );
        },
      },
      {
        title: '助成期限',
        key: 'application_end_date',
        // String width that fits the date "2026/03/31" and skips AppTable's >=80
        // number-width auto-distribute (which would inflate this sparse column).
        width: '88px',
        render: (_, row) => {
          if (!row.is_ng_medical_aid) return null;
          if (!row.nonoutput_rezept_medical_aid_number && row.rezept_kbn !== 2) return null;
          if (!row.application_end_date) return null;
          return (
            <span className="text-red-500">{row.application_end_date.replace(/-/g, '/')}</span>
          );
        },
      },
      {
        title: '状況',
        dataIndex: 'status',
        key: 'status',
        width: 60,
        align: 'center',
        render: value => formatStatus(value),
      },
      {
        title: '施術管理者',
        dataIndex: 'manager_name',
        key: 'manager_name',
        width: 130,
        render: (_, row) => {
          if (!row.rezept_template_value || !/M_J4/.test(row.rezept_template_value)) {
            return null;
          }
          return row.manager_name ? row.manager_name : '登録なし';
        },
      },
      {
        title: '施術師名',
        dataIndex: 'all_massager_names',
        key: 'massager_name',
        width: 140,
        ellipsis: true,
        render: (_: unknown, row: RezeptListItem) => {
          const names = row.all_massager_names || row.massager_name || '';
          return (
            <AppTooltip title={names} showOnlyOnOverflow>
              <div className="max-w-full min-w-0 overflow-hidden break-all truncate line-clamp-1">
                {names}
              </div>
            </AppTooltip>
          );
        },
      },
    ];

    // Center every column header (body alignment stays per-column: only
    // 本・関 / 遅・返 / 状況 set align:'center' for their cells).
    return baseColumns.map(column => ({
      ...column,
      shouldCellUpdate,
      onHeaderCell: () => ({ style: { textAlign: 'center' as const } }),
    }));
  }, [navigate, onToggleTreatmentReport, toCreate, toEdit]);

  // Virtual rows (the list is unpaginated; a busy month is ~700 rows, all of which used to be in
  // the DOM and re-rendered on every checkbox click). Column widths have to be numbers there,
  // so they follow the container width.
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const [scrollbarWidth] = useState(measureNativeScrollbarWidth);

  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    // Ignore 0 (container hidden, e.g. collapsed card) so the table is not torn down meanwhile.
    const update = () => {
      if (el.clientWidth > 0) setContainerWidth(el.clientWidth);
    };
    update();
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // Narrow screens (mobile): as the fixed columns fill the container, the plain table squeezes the
  // shared columns below their padding (a <td> can) and finally collapses them to 0 with a
  // horizontal scroll. Virtual <div> cells can't go narrower than padding + border, so below a
  // comfortable share per column the table is simply the plain one it always was.
  const sharedCount = columns.filter(col => isSharedWidth(col.width)).length;
  const useVirtual =
    sharedCount > 0 &&
    sharedColumnSpace(columns, containerWidth, scrollbarWidth) / sharedCount >= MIN_VIRTUAL_SHARED_WIDTH;
  const fittedColumns = useMemo(
    () => (useVirtual ? fitColumnWidths(columns, containerWidth, scrollbarWidth) : columns),
    [useVirtual, columns, containerWidth, scrollbarWidth]
  );

  // The virtual body always draws its own scrollbar. Where the browser's scrollbar takes layout
  // space, show the browser's own instead, exactly where the plain table had it: a native
  // scroller as wide as a scrollbar over the gutter strip, holding a spacer as tall as the virtual
  // content, its scrollTop kept in step with the virtual holder both ways (the holder is
  // overflow:hidden but rc-virtual-list drives it through scrollTop and listens to its scroll
  // events). The virtual scrollbar is hidden in that mode, see index.scss.
  const nativeScrollRef = useRef<HTMLDivElement>(null);
  const nativeSpacerRef = useRef<HTMLDivElement>(null);
  const tableRef = useRef<{ scrollTo: (config: { top: number }) => void } | null>(null);
  useEffect(() => {
    const container = containerRef.current;
    const scroller = nativeScrollRef.current;
    const spacer = nativeSpacerRef.current;
    if (!container || !scroller || !spacer || scrollbarWidth === 0) return;

    let holder: HTMLElement | null = null;
    const resize = new ResizeObserver(() => layout());

    const layout = () => {
      if (!holder || !holder.isConnected) {
        scroller.style.display = 'none';
        return;
      }
      const top = holder.getBoundingClientRect().top - container.getBoundingClientRect().top;
      scroller.style.display = '';
      scroller.style.top = `${top}px`;
      scroller.style.height = `${holder.clientHeight}px`;
      spacer.style.height = `${holder.scrollHeight}px`;
      if (scroller.scrollTop !== holder.scrollTop) scroller.scrollTop = holder.scrollTop;
    };
    // Each side remembers what it last wrote to the other and ignores the scroll event that write
    // echoes back. Without that, a native smooth scroll (track click, wheel on the bar) was cut
    // short: the holder's late echo carried an older position back onto the scroller mid-animation.
    // The table is moved through antd's scrollTo, not holder.scrollTop: rc-virtual-list keeps its
    // own offset and re-applies it over a direct scrollTop write on its next render.
    let wroteToHolder = -1;
    let wroteToScroller = -1;
    const fromHolder = () => {
      if (!holder) return;
      const top = holder.scrollTop;
      if (Math.abs(top - wroteToHolder) < 1) return;
      if (scroller.scrollTop !== top) {
        wroteToScroller = top;
        scroller.scrollTop = top;
      }
    };
    const fromScroller = () => {
      if (!holder) return;
      const top = scroller.scrollTop;
      if (Math.abs(top - wroteToScroller) < 1) return;
      if (holder.scrollTop !== top) {
        wroteToHolder = top;
        if (tableRef.current) tableRef.current.scrollTo({ top });
        else holder.scrollTop = top;
      }
    };
    // The holder comes and goes (loading / empty list / remount): re-bind when it changes. The
    // mutation callback runs on every row swap while scrolling, so it does nothing else; size
    // changes (rows measured, window resized) arrive through the ResizeObserver.
    const bind = () => {
      const next = container.querySelector<HTMLElement>('.ant-table-tbody-virtual-holder');
      if (next === holder) return;
      holder?.removeEventListener('scroll', fromHolder);
      resize.disconnect();
      holder = next;
      if (holder) {
        // antd puts scrollbar-color on .ant-table, which the plain table's scrolling body
        // inherited; this scroller lives outside it, so copy the value over.
        const table = container.querySelector<HTMLElement>('.ant-table');
        if (table) scroller.style.scrollbarColor = getComputedStyle(table).scrollbarColor;
        holder.addEventListener('scroll', fromHolder, { passive: true });
        resize.observe(holder);
        if (holder.firstElementChild) resize.observe(holder.firstElementChild);
      }
      layout();
    };
    const mutations = new MutationObserver(bind);
    mutations.observe(container, { childList: true, subtree: true });
    scroller.addEventListener('scroll', fromScroller, { passive: true });
    bind();
    // bind() returns early when nothing changed, e.g. no holder at all (plain table on a narrow
    // screen) — make sure the scroller is hidden/placed for the current DOM regardless.
    layout();
    return () => {
      mutations.disconnect();
      resize.disconnect();
      holder?.removeEventListener('scroll', fromHolder);
      scroller.removeEventListener('scroll', fromScroller);
    };
  }, [scrollbarWidth, containerWidth]);

  return (
    <div
      ref={containerRef}
      style={{ position: 'relative', '--rezept-list-scrollbar-width': `${scrollbarWidth}px` } as React.CSSProperties}
    >
      {scrollbarWidth > 0 && (
        <div
          ref={nativeScrollRef}
          className="rezept-list-native-scrollbar"
          style={{
            position: 'absolute',
            right: 0,
            width: scrollbarWidth,
            overflowX: 'hidden',
            overflowY: 'scroll',
            // antd's track colour is translucent; on the plain table it sat on the body's white,
            // here it would show the striped gutter cells underneath.
            background: '#fff',
            zIndex: 3,
            display: 'none',
          }}
        >
          <div ref={nativeSpacerRef} style={{ width: 1 }} />
        </div>
      )}
      {containerWidth > 0 && (
        <AppTable<RezeptListItem>
          // Switching between virtual and plain is a different table, not a prop change.
          key={useVirtual ? 'virtual' : 'plain'}
          // --gutter: the browser's scrollbar takes layout space (Windows/Linux), so keep the
          // header gutter and the native scrollbar where they were; see index.scss.
          className={`rezept-list-table${useVirtual && scrollbarWidth > 0 ? ' rezept-list-table--gutter' : ''}`}
          tableRef={tableRef}
          rowKey="id"
          columns={fittedColumns}
          dataSource={rows}
          loading={isLoading}
          pagination={false}
          hasFilters={hasFilters}
          // Drop horizontal scroll — fit container; overly long cells → ellipsis + tooltip on hover.
          // Virtual needs a numeric x: the columns' total, which is the container width.
          scroll={useVirtual ? { x: containerWidth, y: TABLE_SCROLL_Y } : { y: TABLE_SCROLL_Y }}
          tableLayout="fixed"
          virtual={useVirtual}
          enableEllipsis
          // One eager Tooltip per text cell was about half of the table's render time.
          lazyEllipsisTooltip
          rowSelection={rowSelection}
          // Striping used tr:nth-child, which virtual <div> rows can't use (they are recycled
          // while scrolling): the row's data index decides it instead, see index.scss.
          rowClassName={(row, index) => {
            const stripe = index % 2 === 0 ? 'rezept-row-even' : 'rezept-row-odd';
            if (duplicateTreatmentReportFeeIds.has(row.id)) return `duplicate-treatment-report-fee-row ${stripe}`;
            if (selectedIds.has(row.id)) return `bg-sky-200 ${stripe}`;
            return stripe;
          }}
          onRow={row => ({
            onClick: event => handleRowClick(row, event),
            onDoubleClick: event => handleRowDoubleClick(row, event),
            style: { cursor: 'pointer' },
          })}
        />
      )}
    </div>
  );
};

export default RezeptListTable;
